/**
 * Golden-thread opportunity reference helpers for the Sales Pipeline POC.
 * Keeps existing Mongo `_id` / `refNo`; `pipelineRef` is the continuous display id.
 */

export function suggestPipelineRef(refNo?: string | null, fallbackId?: string): string {
  if (refNo && String(refNo).trim()) {
    const cleaned = String(refNo).replace(/^LEAD[-_]?/i, '').trim();
    if (/^OPP-/i.test(cleaned)) {
      return cleaned.toUpperCase();
    }
    return `OPP-${cleaned}`;
  }
  if (fallbackId) {
    return `OPP-${String(fallbackId).slice(-6).toUpperCase()}`;
  }
  return `OPP-${Date.now().toString().slice(-6)}`;
}

export function displayPipelineRef(item: {
  pipelineRef?: string | null;
  refNo?: string | null;
  _id?: string;
}): string {
  if (item?.pipelineRef && String(item.pipelineRef).trim()) {
    return String(item.pipelineRef).trim();
  }
  return suggestPipelineRef(item?.refNo, item?._id);
}

export type RecordNoType = 'lead' | 'quotation' | 'job' | 'invoice';

const RECORD_NO_PREFIX: Record<RecordNoType, string> = {
  lead: 'LD',
  quotation: 'QT',
  job: 'JB',
  invoice: 'INV',
};

/** Frontend-only number: shared digits from the opportunity ref so LD0007 becomes JB0007. */
export function displayRecordNo(
  type: RecordNoType | string | null | undefined,
  record: { pipelineRef?: string | null; refNo?: string | number | null; _id?: string } | null | undefined
): string {
  const prefix = RECORD_NO_PREFIX[(type || 'lead') as RecordNoType] || 'LD';
  // Prefer the entity's own refNo so JB00001 and JB0901 stay distinct (do not parseInt-collapse).
  const typedRef = record?.refNo == null ? '' : String(record.refNo).trim();
  const source =
    (typedRef && /\d/.test(typedRef) ? typedRef : '') ||
    [record?.pipelineRef, record?.refNo]
      .map(v => (v == null ? '' : String(v)))
      .find(v => /\d/.test(v)) ||
    '';
  if (!source) {
    // Unnumbered drafts used to render as INV0000 / JB0000 — use a stable short id instead.
    if (record?._id) {
      return `${prefix}${String(record._id).slice(-4).toUpperCase()}`;
    }
    return typedRef || '—';
  }
  const digits = (source.match(/\d+/g) || []).pop() || '';
  if (!digits) {
    if (record?._id) {
      return `${prefix}${String(record._id).slice(-4).toUpperCase()}`;
    }
    return typedRef || '—';
  }
  // Keep the original digit run (server uses 5-digit JB pads); only pad short runs to 4.
  const padded = digits.length >= 4 ? digits : digits.padStart(4, '0');
  return `${prefix}${padded}`;
}

export function clientDisplayName(client: any): string {
  if (!client) {
    return '—';
  }
  if (client.type === 'PERSONAL' || client.firstname || client.lastname) {
    return `${client.firstname || ''} ${client.lastname || ''}`.trim() || '—';
  }
  return client.companyName || client.name || '—';
}
